import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useProfile } from '../context/ProfileContext';

/**
 * Blocking screen shown when CaddieSight staff have locked an account out of the app
 * from the ProShop console. The golfer keeps their data but cannot reach any protected
 * page until staff clear the lock. Anyone who isn't actually locked out is bounced back
 * to the app; unauthenticated users are sent to login.
 */
const AccountLockedPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { profile, isLockedOut, isHydrated, logout } = useProfile();

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    } else if (isHydrated && !isLockedOut) {
      navigate('/play', { replace: true });
    }
  }, [isLoading, isAuthenticated, isHydrated, isLockedOut, navigate]);

  const firstName = profile.name?.trim().split(/\s+/)[0] ?? '';

  return (
    <div className="onboarding-page">
      <div className="onboarding-card onboarding-card--narrow">
        <div className="onboarding-card__brand">
          <img src={`${process.env.PUBLIC_URL}/caddiesight-mark.svg`} alt="" className="onboarding-card__mark" />
        </div>
        <div className="onboarding-card__header">
          <h1>{firstName ? `${firstName}, your account is locked` : 'Your account is locked'}</h1>
          <p>
            A CaddieSight pro shop attendant has locked your account. Your data is safe, but you
            can&rsquo;t use the app until it&rsquo;s unlocked. Please reach out to the pro shop for
            help getting back on the course.
          </p>
        </div>

        <div className="onboarding-note onboarding-note--error" role="alert">
          <i className="pi pi-lock" style={{ marginRight: '0.5rem' }} />
          Account locked by CaddieSight staff
        </div>

        <button type="button" className="onboarding-link" onClick={logout}>
          Sign out
        </button>
      </div>
    </div>
  );
};

export default AccountLockedPage;
